/**
 * Reports React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportsService } from '../services/reportsService';
import type { CreateProjectPayload } from '../../../entities/project';
import type { PaginationParams } from '../../../entities/report';

export const reportsKeys = {
    projects: (params?: PaginationParams) => ['reports', 'projects', params] as const,
    project: (id: string) => ['reports', 'project', id] as const,
    projectReports: (id: string, params?: PaginationParams) =>
        ['reports', 'project-reports', id, params] as const,
    report: (id: string) => ['reports', 'report', id] as const,
    dashboardMetrics: ['reports', 'dashboard'] as const,
};

// ── Projects ────────────────────────────────────────────────────────────

/** Fetch the user's projects */
export function useProjects(params?: PaginationParams) {
    return useQuery({
        queryKey: reportsKeys.projects(params),
        queryFn: () => reportsService.getProjects(params),
    });
}

/** Fetch a single project by ID */
export function useProject(projectId: string, enabled = true) {
    return useQuery({
        queryKey: reportsKeys.project(projectId),
        queryFn: () => reportsService.getProjectById(projectId),
        enabled: !!projectId && enabled,
    });
}

/** Create a new project */
export function useCreateProject() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateProjectPayload) => reportsService.createProject(data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['reports', 'projects'] });
        },
    });
}

/** Delete a project */
export function useDeleteProject() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: (projectId: string) => reportsService.deleteProject(projectId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['reports', 'projects'] });
            qc.invalidateQueries({ queryKey: ['reports', 'dashboard'] });
        },
    });
}

// ── Reports ─────────────────────────────────────────────────────────────

/** Fetch reports for a project */
export function useProjectReports(projectId: string, params?: PaginationParams, enabled = true) {
    return useQuery({
        queryKey: reportsKeys.projectReports(projectId, params),
        queryFn: () => reportsService.getProjectReports(projectId, params),
        enabled: !!projectId && enabled,
    });
}

/** Fetch a single report by ID */
export function useReport(reportId: string, enabled = true) {
    return useQuery({
        queryKey: reportsKeys.report(reportId),
        queryFn: () => reportsService.getReportById(reportId),
        enabled: !!reportId && enabled,
    });
}

// ── Dashboard ───────────────────────────────────────────────────────────

/** Fetch aggregated dashboard metrics */
export function useDashboardMetrics() {
    return useQuery({
        queryKey: reportsKeys.dashboardMetrics,
        queryFn: () => reportsService.getDashboardMetrics(),
    });
}
